import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Document, Page, StyleSheet, Text, View, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import Loading from "../../components/Loading";
import useStore from "../../utils/store";
import {
  getAllDispatch,
  orderDispatchAdmin,
  getChallanOrders,
  createChallanApi,
} from "../../utils/api";

export const groupOrdersByCustomer = (ordersList = []) => {
  const groups = [];
  const map = new Map();

  ordersList.forEach((ord) => {
    const custName = ord?.customer?.userName || "Customer";
    const grp = ord?.group || ord?.customer?.group?.name || "";
    const key = `${custName}___${grp}`;
    if (!map.has(key)) {
      const g = { customerName: custName, group: grp, orders: [] };
      map.set(key, g);
      groups.push(g);
    }
    map.get(key).orders.push(ord);
  });
  return groups;
};

// A4 Sheet Side-by-Side Dual Copy with 5px top padding and 60px center gap
const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 5,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  dualWrapper: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "stretch",
    width: "100%",
  },
  slipBox: {
    width: 230.4, // 3.2 inches
    minHeight: 360,
    border: "2.5pt solid #000000",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
  },
  dividerWrapper: {
    width: 45, // approx 60px gap
    minHeight: 360,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  divider: {
    width: "100%",
    height: "100%",
    borderRight: "1.5pt dashed #000000",
  },
  headerSection: {
    padding: 6,
    borderBottom: "2.5pt solid #000000",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  badge: {
    textAlign: "center",
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    backgroundColor: "#000000",
    color: "#ffffff",
    paddingVertical: 2.5,
    paddingHorizontal: 8,
    borderRadius: 2,
    marginVertical: 3,
  },
  dateText: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: "#000000",
  },
  customerBlock: {
    borderBottom: "2.5pt solid #000000",
    padding: 5,
  },
  customerTitle: {
    fontSize: 10.5,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    marginBottom: 3,
  },
  itemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 1.5,
    paddingLeft: 4,
  },
  itemText: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    flex: 1,
  },
  qtyText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
    textAlign: "right",
  },
  weightBox: {
    borderBottom: "2.5pt solid #000000",
    paddingVertical: 8,
    paddingHorizontal: 5,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
  },
  weightText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: "#000000",
  },
  cartageText: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: "#000000",
    marginTop: 2,
  },
  queryText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: "#000000",
    marginTop: 10,
  },
  signSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: 6,
  },
  signText: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
  },
});

const isTagOrder = (o) => {
  const prodGroup = (o?.product?.group || "").toLowerCase().trim();
  const ordGroup = (o?.group || "").toLowerCase().trim();
  const prodName = (o?.product?.productName || o?.description || "").toLowerCase().trim();
  return prodGroup === "tag" || ordGroup === "tag" || prodName.includes("tag");
};

const getCustomerWeight = (cg, allOrders, inputWeight) => {
  const parsedWeight = parseFloat(inputWeight);
  const hasInputWeight = !isNaN(parsedWeight) && parsedWeight > 0;
  const cgOrdersWeight = cg.orders.reduce(
    (sum, o) => sum + (parseFloat(o?.challanWeight || o?.weight || 0) || 0),
    0
  );

  if (hasInputWeight) {
    const totalTagQty = allOrders.filter(isTagOrder).reduce(
      (s, o) => s + (parseFloat(o?.quantity) || 0),
      0
    );
    const totalAllQty = allOrders.reduce(
      (s, o) => s + (parseFloat(o?.quantity) || 0),
      0
    );
    const custTagQty = cg.orders.filter(isTagOrder).reduce(
      (s, o) => s + (parseFloat(o?.quantity) || 0),
      0
    );
    const custAllQty = cg.orders.reduce(
      (s, o) => s + (parseFloat(o?.quantity) || 0),
      0
    );

    if (totalTagQty > 0) {
      return custTagQty > 0 ? Number(((custTagQty * parsedWeight) / totalTagQty).toFixed(3)) : 0;
    } else if (totalAllQty > 0) {
      return Number(((custAllQty * parsedWeight) / totalAllQty).toFixed(3));
    } else {
      return parsedWeight;
    }
  } else if (cgOrdersWeight > 0) {
    return Number(cgOrdersWeight.toFixed(3));
  }
  return 0;
};

const SlipSection = ({ title, customerGroups, displayWeight, params, dateStr }) => {
  return (
    <View style={pdfStyles.slipBox}>
      <View>
        {/* Header Block */}
        <View style={pdfStyles.headerSection}>
          <Text style={pdfStyles.headerTitle}>PRABHU LABELS</Text>
          <Text style={pdfStyles.badge}>{title}</Text>
          <Text style={pdfStyles.dateText}>Date: {dateStr}</Text>
        </View>

        {/* Customer & Items Blocks */}
        {customerGroups.map((cg, cIdx) => (
          <View key={cIdx} style={pdfStyles.customerBlock}>
            <Text style={pdfStyles.customerTitle}>
              {cg.customerName} {cg.group ? `(${cg.group})` : ""}
            </Text>
            {cg.orders.map((ord, idx) => (
              <View key={idx} style={pdfStyles.itemRow}>
                <Text style={pdfStyles.itemText}>
                  {ord?.product?.productName || ord?.description || "Product"}
                </Text>
                <Text style={pdfStyles.qtyText}>{ord?.quantity || 0}</Text>
              </View>
            ))}
          </View>
        ))}

        {/* Weight & Query Section */}
        <View style={pdfStyles.weightBox}>
          {displayWeight && displayWeight > 0 ? (
            <Text style={pdfStyles.weightText}>Weight: {displayWeight} kg</Text>
          ) : null}
          {params?.cartage && params?.cartage !== "0" && (
            <Text style={pdfStyles.cartageText}>Cartage: Rs. {params.cartage}</Text>
          )}
          <Text style={pdfStyles.queryText}>Call for query: 9417771111</Text>
        </View>
      </View>

      {/* Footer Signatures */}
      <View style={pdfStyles.signSection}>
        <Text style={pdfStyles.signText}>Receiver's Sign</Text>
      </View>
    </View>
  );
};

const MyDualDocument = ({ dash, params, isCartage }) => {
  const dateStr = dash?.updatedAt
    ? new Date(dash.updatedAt).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  const title = isCartage ? "CARTAGE SLIP" : "DISPATCH CHALLAN";
  const ordersList = dash?.allOrders?.length ? dash.allOrders : [dash];
  const customerGroups = groupOrdersByCustomer(ordersList);

  const parsedWeight = parseFloat(params?.weight);
  const hasInputWeight = !isNaN(parsedWeight) && parsedWeight > 0;
  let displayWeight = 0;
  if (hasInputWeight) {
    displayWeight = parsedWeight;
  } else {
    const sumW = ordersList.reduce(
      (sum, o) => sum + (parseFloat(o?.challanWeight || o?.weight || 0) || 0),
      0
    );
    if (sumW > 0) displayWeight = Number(sumW.toFixed(3));
  }

  return (
    <Document title={`${dash?.customer?.userName || "dispatch"}-3.2x5-challan`}>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.dualWrapper}>
          <SlipSection
            title={title}
            customerGroups={customerGroups}
            displayWeight={displayWeight}
            params={params}
            dateStr={dateStr}
          />
          <View style={pdfStyles.dividerWrapper}>
            <View style={pdfStyles.divider} />
          </View>
          <SlipSection
            title={title}
            customerGroups={customerGroups}
            displayWeight={displayWeight}
            params={params}
            dateStr={dateStr}
          />
        </View>
      </Page>
    </Document>
  );
};

const ChallanPdfView = () => {
  const [dash, setDash] = useState(null);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isExportingPng, setIsExportingPng] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);

  const isCartage =
    location.pathname.includes("cartage") ||
    params.type === "cartage" ||
    (params.cartage && params.cartage !== "0");

  const getData = async () => {
    setLoading(true);
    try {
      let currentOrder = null;
      let orderGroup = null;
      let ordersList = [];

      const targetOrderIds =
        params.order && params.order !== "all"
          ? params.order.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      // 1. Fetch from getChallanOrders
      const [challanSuccess, challanData] = await getChallanOrders(startDate, endDate);
      const [allChallanSuccess, allChallanData] =
        !challanSuccess || !challanData?.length ? await getChallanOrders() : [false, []];
      const combinedChallan = [
        ...(Array.isArray(challanData) ? challanData : []),
        ...(Array.isArray(allChallanData) ? allChallanData : []),
      ];

      // 2. Fetch from getAllDispatch
      const [success, data] = await getAllDispatch(startDate, endDate);
      const [allSuccess, allData] =
        !success || !data?.length ? await getAllDispatch() : [false, []];
      const combinedDispatch = [
        ...(Array.isArray(data) ? data : []),
        ...(Array.isArray(allData) ? allData : []),
      ];

      // 3. Fetch from orderDispatchAdmin
      const [adminSuccess, adminData] = await orderDispatchAdmin(startDate, endDate);
      const combinedAdmin = Array.isArray(adminData) ? adminData : [];

      const allGroups = [...combinedChallan, ...combinedDispatch, ...combinedAdmin];

      // Strategy 1: Match by exact targetOrderIds if passed
      if (targetOrderIds.length > 0) {
        const foundMap = new Map();
        for (const g of allGroups) {
          const gOrders = g.orders || (g._id && !Array.isArray(g.orders) ? [g] : []);
          for (const ord of gOrders) {
            const ordIdStr = String(ord._id || ord.id);
            if (targetOrderIds.includes(ordIdStr) && !foundMap.has(ordIdStr)) {
              foundMap.set(ordIdStr, ord);
            }
          }
        }

        const matchedOrders = targetOrderIds
          .map((id) => foundMap.get(id))
          .filter(Boolean);

        if (matchedOrders.length > 0) {
          ordersList = matchedOrders;
          currentOrder = matchedOrders[0];
        }
      }

      // Strategy 2: Match by Challan ID or Customer / Group ID
      if (!currentOrder && params.id) {
        const decodedId = decodeURIComponent(params.id);
        for (const g of allGroups) {
          const gChallanId = g.challanId || g._id?.challanKey || "";
          const gPhone = g._id?.phoneNumber || g.customer?.phoneNumber || "";
          const gIdStr = String(g._id || g.id || "");

          if (
            (gChallanId && gChallanId === decodedId) ||
            (gIdStr && gIdStr === decodedId) ||
            (gPhone && gPhone === decodedId) ||
            (g.orders?.some((o) => o.challanId === decodedId || String(o._id) === decodedId))
          ) {
            orderGroup = g;
            break;
          }
        }

        if (orderGroup?.orders?.length > 0) {
          ordersList = orderGroup.orders;
          currentOrder = orderGroup.orders[0];
        }
      }

      if (currentOrder) {
        setDash({ ...currentOrder, allOrders: ordersList.length > 0 ? ordersList : [currentOrder] });
        const targetIds = ordersList?.length > 0 ? ordersList.map((o) => o._id) : [currentOrder._id];
        if (targetIds.length > 0) {
          createChallanApi(targetIds).catch(console.error);
        }
      } else {
        setDash(null);
      }
    } catch (e) {
      console.error("Error fetching challan PDF data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [startDate, endDate, params.id, params.order]);

  const handlePrint = () => {
    window.print();
  };

  const saveFile = async () => {
    setIsExportingPdf(true);
    try {
      const blob = await pdf(
        <MyDualDocument dash={dash} params={params} isCartage={isCartage} />
      ).toBlob();
      saveAs(
        blob,
        `${dash?.customer?.userName || "dispatch"}-3.2x5-challan.pdf`
      );
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const downloadPng = async () => {
    const node = document.getElementById("printable-dual-slip");
    if (!node) return;
    setIsExportingPng(true);
    try {
      const dataUrl = await toPng(node, {
        pixelRatio: 3,
        backgroundColor: "#ffffff",
      });
      saveAs(
        dataUrl,
        `${dash?.customer?.userName || "challan"}-3.2x5-dual.png`
      );
    } catch (error) {
      console.error("Error downloading PNG:", error);
    } finally {
      setIsExportingPng(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!dash) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 font-sans">
        <div className="bg-white p-6 rounded-2xl shadow-xl text-center max-w-sm w-full border border-gray-200">
          <div className="text-4xl mb-3">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Details Not Found
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Order ID:{" "}
            <span className="font-mono font-semibold text-gray-700">
              {params.order || params.id}
            </span>
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={getData}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-4 rounded-xl shadow transition active:scale-95 cursor-pointer"
            >
              🔄 Retry Loading
            </button>
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-xl transition cursor-pointer"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dateStr = dash?.updatedAt
    ? new Date(dash.updatedAt).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  const title = isCartage ? "CARTAGE SLIP" : "DISPATCH CHALLAN";
  const ordersList = dash?.allOrders?.length ? dash.allOrders : [dash];
  const customerGroups = groupOrdersByCustomer(ordersList);
  const parsedWeight = parseFloat(params?.weight);
  const hasInputWeight = !isNaN(parsedWeight) && parsedWeight > 0;
  let displayWeight = 0;
  if (hasInputWeight) {
    displayWeight = parsedWeight;
  } else {
    const sumW = ordersList.reduce(
      (sum, o) => sum + (parseFloat(o?.challanWeight || o?.weight || 0) || 0),
      0
    );
    if (sumW > 0) displayWeight = Number(sumW.toFixed(3));
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center pb-12 font-sans">
      {/* Top Action Bar */}
      <div className="w-full max-w-3xl p-3 bg-white shadow-md flex items-center justify-between gap-3 sticky top-0 z-50 no-print">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 p-2 rounded-lg text-xs font-bold transition cursor-pointer"
            title="Go Back"
          >
            ⬅️ Back
          </button>
          <div>
            <h2 className="text-sm font-bold text-gray-800">
              3.2" x 5" Dual Copy ({isCartage ? "Cartage" : "Challan"})
            </h2>
            <span className="text-[11px] text-gray-500">
              Customer Copy + Admin Copy ({customerGroups.length} Customer{customerGroups.length > 1 ? "s" : ""})
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded-lg shadow flex items-center gap-1 text-xs transition active:scale-95 cursor-pointer"
          >
            <span>🖨️ Print Slip</span>
          </button>
          <button
            onClick={downloadPng}
            disabled={isExportingPng}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-3 rounded-lg shadow flex items-center gap-1 text-xs transition active:scale-95 cursor-pointer"
          >
            <span>{isExportingPng ? "..." : "🖼️ PNG"}</span>
          </button>
          <button
            onClick={saveFile}
            disabled={isExportingPdf}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-lg shadow flex items-center gap-1 text-xs transition active:scale-95 cursor-pointer"
          >
            <span>{isExportingPdf ? "Generating..." : "📥 Download PDF"}</span>
          </button>
        </div>
      </div>

      {/* 3.2" x 5" Side-by-Side Dual HTML Slips (Printable) */}
      <div id="printable-dual-slip" className="w-full flex items-stretch justify-center py-6 print-page-wrapper" style={{ paddingTop: "5px" }}>
        <div
          className="printable-single-slip-group bg-white text-black p-4 border border-gray-300 shadow-xl font-sans flex items-stretch justify-center print:border-none print:shadow-none print:p-0"
          style={{
            width: "fit-content",
            maxWidth: "100%",
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          {/* Left Slip (3.2" x 5") */}
          <div
            className="border-[2.5px] border-black flex flex-col justify-between bg-white text-black"
            style={{ width: "3.2in", minWidth: "3.2in", maxWidth: "3.2in", minHeight: "4.8in", boxSizing: "border-box" }}
          >
            <div>
              {/* Header */}
              <div className="p-2 border-b-[2.5px] border-black flex flex-col items-center">
                <h1 className="text-base font-black tracking-tight text-center text-black">
                  PRABHU LABELS
                </h1>
                <div className="bg-black text-white text-[10px] font-black tracking-wider px-3 py-0.5 rounded text-center my-1 print:bg-black print:text-white">
                  {title}
                </div>
                <div className="text-[11px] font-black text-black text-center">
                  Date: {dateStr}
                </div>
              </div>

              {/* Customer Blocks */}
              {customerGroups.map((cg, cIdx) => (
                <div key={cIdx} className="p-2 border-b-[2.5px] border-black">
                  <div className="text-xs font-black text-black mb-1">
                    {cg.customerName} {cg.group ? `(${cg.group})` : ""}
                  </div>
                  <div className="flex flex-col gap-1 pl-1">
                    {cg.orders.map((ord, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs font-bold text-black">
                        <span className="font-extrabold text-black">
                          {ord?.product?.productName || ord?.description || "Product"}
                        </span>
                        <span className="text-sm font-black text-black">{ord?.quantity || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Weight & Query Box */}
              <div className="p-2 border-b-[2.5px] border-black flex flex-col items-center justify-between min-h-[60px]">
                {displayWeight && displayWeight > 0 ? (
                  <div className="text-sm font-black text-black text-center">
                    Weight: {displayWeight} kg
                  </div>
                ) : null}
                {params?.cartage && params?.cartage !== "0" && (
                  <div className="text-xs font-black text-orange-700 print:text-black text-center">
                    Cartage: Rs. {params.cartage}
                  </div>
                )}
                <div className="text-[10px] font-black text-black text-center mt-3">
                  Call for query: 9417771111
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-2 flex justify-end items-center text-[11px] font-black text-black">
              <span>Receiver's Sign</span>
            </div>
          </div>

          {/* Center Cut Line with GUARANTEED 60px WIDE SPACE */}
          <div
            className="flex flex-col items-center justify-center relative shrink-0"
            style={{ width: "60px", minWidth: "60px", maxWidth: "60px", boxSizing: "border-box" }}
          >
            <div className="h-full border-r-2 border-dashed border-black"></div>
            <span className="absolute text-[8px] text-black font-bold -rotate-90 whitespace-nowrap bg-white px-1 no-print">
              ✂ CUT / FOLD
            </span>
          </div>

          {/* Right Slip (3.2" x 5") */}
          <div
            className="border-[2.5px] border-black flex flex-col justify-between bg-white text-black"
            style={{ width: "3.2in", minWidth: "3.2in", maxWidth: "3.2in", minHeight: "4.8in", boxSizing: "border-box" }}
          >
            <div>
              {/* Header */}
              <div className="p-2 border-b-[2.5px] border-black flex flex-col items-center">
                <h1 className="text-base font-black tracking-tight text-center text-black">
                  PRABHU LABELS
                </h1>
                <div className="bg-black text-white text-[10px] font-black tracking-wider px-3 py-0.5 rounded text-center my-1 print:bg-black print:text-white">
                  {title}
                </div>
                <div className="text-[11px] font-black text-black text-center">
                  Date: {dateStr}
                </div>
              </div>

              {/* Customer Blocks */}
              {customerGroups.map((cg, cIdx) => (
                <div key={cIdx} className="p-2 border-b-[2.5px] border-black">
                  <div className="text-xs font-black text-black mb-1">
                    {cg.customerName} {cg.group ? `(${cg.group})` : ""}
                  </div>
                  <div className="flex flex-col gap-1 pl-1">
                    {cg.orders.map((ord, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs font-bold text-black">
                        <span className="font-extrabold text-black">
                          {ord?.product?.productName || ord?.description || "Product"}
                        </span>
                        <span className="text-sm font-black text-black">{ord?.quantity || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Weight & Query Box */}
              <div className="p-2 border-b-[2.5px] border-black flex flex-col items-center justify-between min-h-[60px]">
                {displayWeight && displayWeight > 0 ? (
                  <div className="text-sm font-black text-black text-center">
                    Weight: {displayWeight} kg
                  </div>
                ) : null}
                {params?.cartage && params?.cartage !== "0" && (
                  <div className="text-xs font-black text-orange-700 print:text-black text-center">
                    Cartage: Rs. {params.cartage}
                  </div>
                )}
                <div className="text-[10px] font-black text-black text-center mt-3">
                  Call for query: 9417771111
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-2 flex justify-end items-center text-[11px] font-black text-black">
              <span>Receiver's Sign</span>
            </div>
          </div>
        </div>
      </div>

      {/* Print CSS for Dual Copy with 5px top padding and 60px center gap */}
      <style>
        {`
          @media print {
            body, html {
              background: white !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
            }
            .no-print, button, [class*="InstallPWA"], [data-pwa] {
              display: none !important;
            }
            .print-page-wrapper {
              width: 100% !important;
              display: flex !important;
              justify-content: center !important;
              align-items: flex-start !important;
              padding-top: 5px !important;
              margin: 0 auto !important;
            }
            #printable-dual-slip {
              width: fit-content !important;
              max-width: 100% !important;
              margin: 5px auto 0 auto !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
              box-sizing: border-box !important;
              display: flex !important;
              flex-direction: row !important;
              justify-content: center !important;
              align-items: stretch !important;
            }
            #printable-dual-slip > div:first-child,
            #printable-dual-slip > div:last-child {
              width: 3.2in !important;
              min-width: 3.2in !important;
              max-width: 3.2in !important;
              height: 4.8in !important;
              min-height: 4.8in !important;
              box-sizing: border-box !important;
            }
            #printable-dual-slip > div:nth-child(2) {
              width: 60px !important;
              min-width: 60px !important;
              max-width: 60px !important;
              box-sizing: border-box !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ChallanPdfView;
